/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import loadable from '@loadable/component'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Components
import { Skeleton } from '@salesforce/retail-react-app/app/components/shared/ui'
import { configureRoutes } from '@salesforce/retail-react-app/app/utils/routes-utils'
import { routes as _routes } from '@salesforce/retail-react-app/app/routes'

const fallback = <Skeleton height="75vh" width="100%" />

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
const Home = loadable(() => import('./pages/home'), { fallback })
const Preview = loadable(() => import('./pages/preview'), { fallback })
const Cart = loadable(() => import('./pages/cart'), { fallback })
const Checkout = loadable(() => import('./pages/checkout'), {
    fallback
})
const Confirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback
})
const ProductList = loadable(() => import('./pages/product-list'), {
    fallback
})
const ProductDetail = loadable(() => import('./pages/product-detail'), { fallback })
const SSOCallback = loadable(() => import('./pages/sso-callback'))
const AdyenDropIn = loadable(() => import('./integrations/adyen/components/dropin'))
const Auth = loadable(() => import('./pages/auth'))
const EmailConfirm = loadable(() => import('./pages/email-confirm'))
const Account = loadable(() => import('./pages/account'), { fallback })
const ResetPassword = loadable(() => import('./pages/reset-password'), { fallback })
const MyRewards = loadable(() => import('./pages/my-rewards/index'), { fallback })
const StoreLocator = loadable(() => import('./pages/store-locator/index'), { fallback })
const PrivacyPolicy = loadable(() => import('./pages/privacy-policy/index.jsx'), { fallback })
const ContentPage = loadable(() => import('./pages/content-page'), { fallback })
const ShareWishlist = loadable(() => import('./pages/share-wishlist/index.jsx'), { fallback })
const Sitemap = loadable(() => import('./pages/sitemap/index.jsx'), { fallback })
const EmailUs = loadable(() => import('./pages/email-us/index.jsx'), { fallback })

const PageNotFound = loadable(() => import('./pages/page-not-found'))
const DSSOrderActionPage = loadable(
    () => import('./pages/account/dss/dss-order-action-page.jsx'),
    {fallback}
)
const EswEmbeddedCheckout = loadable(() => import('./pages/esw-embedded-checkout/index.jsx'), { fallback })

const routes = [
    {
        path: '/',
        component: Home,
        exact: true
    },
    {
        path: '/:pageName.html',
        component: ContentPage,
        exact: true
    },
    {
        path: '/ui/CMS/Content/*',
        component: Preview
    },
    {
        path: '/cart',
        component: Cart,
        exact: true
    },
    {
        path: '/p/:productName/:productId.html',
        component: ProductDetail
    },
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: Confirmation,
        exact: true
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/:brandId([g|d]{1})/:categoryPath(.*)/',
        component: ProductList
    },
    {
        path: '/category/:categoryId',
        component: ProductList
    },
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/sso-callback',
        component: SSOCallback
    },
    {
        path: '/adyen',
        component: AdyenDropIn
    },
    {
        path: '/login',
        component: Auth,
        exact: true
    },
    {
        path: '/registration',
        component: Auth,
        exact: true
    },
    {
        path: '/email-confirm',
        component: EmailConfirm,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/privacy-policy',
        component: PrivacyPolicy,
        exact: true
    },
    {
        path: '/email-us',
        component: EmailUs,
        exact: true
    },
    {
        path: '/account',
        component: Account
    },
    {
        path: '/my-rewards',
        component: MyRewards
    },
    {
        path: '/share-wishlist/:wishlistId',
        component: ShareWishlist,
        exact: true
    },
    {
        path: '/esw-checkout',
        component: EswEmbeddedCheckout,
        exact: true
    },
    {
        path: '/sitemap',
        component: Sitemap,
        exact: true
    },
    {
        path: '/dss-order-action-page/:orderNo',
        component: DSSOrderActionPage,
        exact: true
    },
]

export default () => {
    const config = getConfig()

    if (config.app.STORELOCATOR_ENABLED === 'true') {
        routes.push({
            path: '/stores',
            component: StoreLocator
        })
    }

    /* Must be the last one */
    routes.push({
        path: '*',
        component: PageNotFound
    })

    routes.push(..._routes)

    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
